import React from 'react';
import PropTypes from 'prop-types';
import {computed, observable, reaction} from 'mobx';
import {observer, inject} from 'mobx-react';
import LazyLoad, {forceCheck} from 'react-lazyload';

import {cn} from 'spa/css';

import './progressive-background-image.less';

const HIGH_RES_SCREEN_MEDIA_QUERY = 'only screen and (-webkit-min-device-pixel-ratio: 2), only screen and (min--moz-device-pixel-ratio: 2), only screen and (-o-min-device-pixel-ratio: 2/1), only screen and (min-device-pixel-ratio: 2), only screen and (min-resolution: 192dpi), only screen and (min-resolution: 2dppx)';

@inject(({
  store,
  store: {
    initialLoadComplete,
    isAmpPage,
    screen,
  },
}) => (
  {
    initialLoadComplete,
    isAmpPage,
    screen,
    store,
  }
))
@observer
class ProgressiveBackgroundImage extends React.Component {
  // Proptypes info:
  //
  // disableWebp - disable use of webp images
  // highResImg - normal image URL
  // highResImg2x - @2x image URL
  // lowResImg - low quality image url used for pre-render
  //
  // mediaQueries - array of media queries, ordered by descending value of width.
  // It should be in the following format, for example:
  // [{
  //   query: '(max-width: 600px)',
  //   highResImg: 'https://res.cloudinary.com/huxr6hrje/image/upload/f_auto,w_600/v1527067430/b2c-art/landing-pages/1x/Desktop-Hero_1x-min-min-min.jpg',
  //   highResImg2x: 'https://res.cloudinary.com/huxr6hrje/image/upload/f_auto,w_1200/v1526982871/b2c-art/landing-pages/Desktop-Hero_2x-min-min.jpg',
  // }]

  static propTypes = {
    children: PropTypes.node,
    customClass: PropTypes.string,
    customClassForAmp: PropTypes.string,
    disableWebp: PropTypes.bool,
    initialLoadComplete: PropTypes.bool,
    isAboveFold: PropTypes.bool,
    isAmpPage: PropTypes.bool,
    highResImg: PropTypes.string,
    highResImg2x: PropTypes.string,
    lowResImg: PropTypes.string,
    mediaQueries: PropTypes.array,
    screen: PropTypes.shape({
      supportsWebp: PropTypes.bool,
      width: PropTypes.number,
    }),
    store: PropTypes.shape({
      initialLoadComplete: PropTypes.bool,
    }),
  };

  UNSAFE_componentWillMount() {
    const {initialLoadComplete, lowResImg} = this.props;

    this.selectedLowRes = lowResImg;

    if (typeof document !== 'undefined' && document.readyState === 'complete' && !initialLoadComplete) {
      this.props.store.initialLoadComplete = true;
    }
  }

  componentDidMount() {
    this.listeners = [
      reaction(
        () => this.props.initialLoadComplete,
        (initialLoadComplete) => {
          if (initialLoadComplete) {
            this.showHighRes = true;
          }
        },
        {
          fireImmediately: true,
          name: 'Check if content has finished loading',
        },
      ),
      reaction(
        () => this.props.screen.width,
        (width) => {
          if (width !== this.lastWidth) {
            this.lastWidth = width;

            this.getBackground(width);
          }
        },
        {
          fireImmediately: true,
          name: 'Get image for new screen width',
        },
      ),
      reaction(
        () => this.props.screen.supportsWebp,
        (supportsWebp) => {
          if (supportsWebp) {
            this.getBackground(this.props.screen.width);
          }
        },
        {
          fireImmediately: true,
          name: 'Get image in webp',
        },
      ),
    ];
  }

  componentDidUpdate() {
    forceCheck();
  }

  componentWillUnmount() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }

  // Get image url in webp format
  getWebpImage = (url) => {
    const imageFilename = url.substring(url.lastIndexOf('/') + 1);

    const imageExtension = imageFilename.substring(imageFilename.lastIndexOf('.') + 1);

    return url.replace(imageExtension, 'webp');
  }

  // Set the correct background image depending on the current state
  getBackground = (width) => {
    const {
      disableWebp, highResImg, highResImg2x, mediaQueries,
    } = this.props;

    let tempHighRes = highResImg;
    let tempHighRes2x = highResImg2x;

    if (mediaQueries) {
      if (typeof window === 'undefined') return null;

      mediaQueries.forEach((mediaQuery) => {
        if (window.matchMedia(mediaQuery.query).matches) {
          tempHighRes = mediaQuery.highResImg;
          tempHighRes2x = mediaQuery.highResImg2x;
        }
      });
    }

    if (this.props.screen.supportsWebp && !disableWebp) {
      this.selectedHighRes = tempHighRes ? this.getWebpImage(tempHighRes) : null;
      this.selectedHighRes2x = tempHighRes2x ? this.getWebpImage(tempHighRes2x) : null;
    } else {
      this.selectedHighRes = tempHighRes || null;
      this.selectedHighRes2x = tempHighRes2x || null;
    }
  }

  @observable selectedLowRes;
  @observable selectedHighRes;
  @observable selectedHighRes2x;
  @observable showHighRes = false;
  @observable useWebp = false;

  @computed get backgroundImage() {
    const {
      selectedHighRes, selectedHighRes2x, use2xImage,
    } = this;

    if (!selectedHighRes) return null;

    return (use2xImage && selectedHighRes2x) ? selectedHighRes2x : selectedHighRes;
  }

  @computed get use2xImage() {
    if (typeof window === 'undefined') return null;

    const {showHighRes} = this;

    return showHighRes && window.matchMedia(HIGH_RES_SCREEN_MEDIA_QUERY).matches;
  }

  @computed get appliedStyles() {
    const {isAboveFold} = this.props;
    const {
      backgroundImage,
      selectedLowRes,
      showHighRes,
    } = this;

    if (!backgroundImage) return null;

    if (isAboveFold) {
      return {
        backgroundImage: `url(${backgroundImage})`,
      };
    }

    return {
      backgroundImage: `url(${showHighRes ? backgroundImage : selectedLowRes})`,
    };
  }

  render() {
    const {
      appliedStyles,
      backgroundImage,
      selectedLowRes,
      showHighRes,
    } = this;

    const {
      children,
      customClass,
      customClassForAmp,
      disableWebp,
      highResImg,
      highResImg2x,
      initialLoadComplete,
      isAboveFold,
      isAmpPage,
      lowResImg,
      mediaQueries,
      screen,
      store,
      ...rest
    } = this.props;

    if (isAmpPage) {
      if (!customClassForAmp) {
        rest.style = {backgroundImage: `url(${highResImg2x})`};
      }
    }

    if (isAboveFold) {
      return (
        <div
          className={cn(
            'progressive-background-image__container',
            customClass,
          )}
          style={appliedStyles}
          {...rest}
        >
          {children}
        </div>
      );
    }

    return isAmpPage ?
      (
        <div
          className={cn(
            'progressive-background-image__container',
            customClass,
            customClassForAmp,
          )}
          {...rest}
        >
          {children}
        </div>
      ) : (
        <LazyLoad
          offset={100}
          placeholder={
            <div
              className={cn(
                'progressive-background-image__container',
                customClass,
              )}
              {...rest}
            >
              {children}
            </div>
          }>
          <div
            className={cn(
              'progressive-background-image__container',
              customClass,
            )}
            style={appliedStyles}
            {...rest}
          >
            {children}
          </div>
        </LazyLoad>
      );
  }
}

export default ProgressiveBackgroundImage;



// WEBPACK FOOTER //
// ./spa/Components/ProgressiveBackgroundImage.jsx